import { useEffect, useState } from 'react';
import _ from 'lodash';
import {
  useAdminResetStripeDataMutation,
  useAdminUpdateInfluencerMutation,
  useGetAdministrationDataQuery,
  GetAdministrationDataQuery,
  AdminInfluencerUpdateInput,
  Customizations,
} from 'generated/graphql';
import { Actions, Async, FSpread } from '@client/new/helpers/types';
import { muiForm } from '@client/new/helpers/forms';
import {
  useForm,
  Control,
  UseFormRegister,
  UseFormWatch,
  FieldErrors,
} from 'react-hook-form';
import moment from 'moment';
import toast from 'react-hot-toast';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { track } from '@services/mixpanel';
import * as enums from '@playbook-technologies/common-enums';

export type AdministrationDataResult = GetAdministrationDataQuery['creator'];

export type InitialForm = {
  isVisible: string;
  publishedAt: string | null;
  mode: number | null;
  tier: number | null;
  isTCAccepted: boolean;
  isPaymentEnabled: boolean;
  paymentMethod: number | null;
  customURL: string | null;
  profileDeepLink: string | null;
  tags: string[];
  customizations: object;
};

export type AdministrationModel = {
  register: UseFormRegister<InitialForm>;
  watch: UseFormWatch<InitialForm>;
  control: Control<InitialForm, any, InitialForm>;
  submittingReset: boolean;
  submittingUpdate: boolean;
  stripeUserId: string | null;
  errors: FieldErrors<InitialForm>;
};

export type UseAdministrationModelParams = {
  influencerId: number;
};

type PayloadMap = {
  resetStripe: null;
  updateInfluencer: null;
  setCustomizations: object;
  setTags: string[];
};

export type AdministrationAction = Actions<PayloadMap>;
export type AdministrationDispatcher = FSpread<PayloadMap>;

const emptyData: InitialForm = {
  isVisible: 'No',
  publishedAt: null,
  mode: null,
  tier: null,
  isTCAccepted: false,
  isPaymentEnabled: false,
  paymentMethod: null,
  customURL: null,
  profileDeepLink: null,
  tags: [],
  customizations: {},
};

const omitTypenameFromCustomizations = (customizations: Customizations) => {
  const cleanCustomizations = _.omit(customizations, ['__typename']);
  if (cleanCustomizations.productCatalog.length) {
    cleanCustomizations.productCatalog = cleanCustomizations.productCatalog.map(
      (item) => _.omit(item, ['__typename']),
    );
  }
  return cleanCustomizations;
};

const initialFormData = (creator: AdministrationDataResult): InitialForm => {
  const customizations: InitialForm['customizations'] = creator?.customizations
    ? omitTypenameFromCustomizations(creator.customizations)
    : {};

  return {
    isVisible: creator?.isVisible ? 'Yes' : 'No',
    publishedAt: creator?.publishedAt
      ? moment(creator?.publishedAt).format(moment.HTML5_FMT.DATETIME_LOCAL)
      : null,
    mode: creator?.mode || null,
    tier: creator?.tier || null,
    isTCAccepted: !!creator?.isTCAccepted,
    isPaymentEnabled: !!creator?.isPaymentEnabled,
    paymentMethod: creator?.paymentMethod || null,
    customURL: creator?.customURL || null,
    profileDeepLink: creator?.profileDeepLink || null,
    tags: creator?.tags?.filter((i) => i !== null) || [],
    customizations,
  };
};

const valueToPayload = (prop: string, values: any) => {
  switch (prop) {
    case 'isVisible':
      return values[prop] === 'Yes';
    default:
      return values[prop];
  }
};

const mapValuesToPayload = (values: any, defaultValues: any) => {
  const payload: Record<string, any> = {};
  for (const prop in values) {
    if (JSON.stringify(defaultValues[prop]) !== JSON.stringify(values[prop])) {
      const payloadValue = valueToPayload(prop, values);
      payload[prop] = payloadValue;
    }
  }
  return payload;
};

const administrationSchema = z.object({
  profileDeepLink: z
    .string()
    .regex(/^https:\/\/playbook\.[a-zA-Z0-9_-]*app\.link\/[a-zA-Z0-9_-]+$/, {
      message: 'Required format https://playbook.app.link/<customurl>',
    }),
});

export const useAdministrationModel = (
  p: UseAdministrationModelParams,
): Async<AdministrationModel & { dispatch: AdministrationDispatcher }> => {
  const [submittingReset, setSubmitingReset] = useState(false);
  const [submittingUpdate, setSubmitingUpdate] = useState(false);
  const [adminResetStripeDataMutation] = useAdminResetStripeDataMutation();
  const [adminUpdateInfluencerMutation] = useAdminUpdateInfluencerMutation();

  const { data, loading, error, refetch } = useGetAdministrationDataQuery({
    fetchPolicy: 'cache-and-network',
    variables: { id: p.influencerId },
    skip: !p.influencerId,
  });

  const {
    register,
    setValue,
    watch,
    control,
    reset,
    formState,
    getValues,
    handleSubmit,
    clearErrors,
  } = muiForm(
    useForm<InitialForm>({
      defaultValues: emptyData,
      resolver: zodResolver(administrationSchema),
    }),
  );

  useEffect(() => {
    if (data?.creator) {
      reset(initialFormData(data.creator));
    }
  }, [data, reset]);

  useEffect(() => {
    if (!_.isEmpty(formState.errors)) {
      toast.error('Please fix form errors!');
    }
  }, [formState.errors]);

  if (loading) {
    return {
      type: 'fetching',
    };
  }

  if (error) {
    return {
      type: 'fetch-error',
      value: 'Could not fetch data',
    };
  }

  const onUpdate = handleSubmit(() => {
    const values = getValues();
    const updatedFields: Partial<Omit<InitialForm, 'isVisible'>> =
      mapValuesToPayload(values, formState.defaultValues);

    if (_.isEmpty(updatedFields)) {
      toast('No changes to be applied');
      return;
    }

    if (updatedFields.mode === enums.Influencer.Mode.Live.value) {
      const { profileReady, paymentMethodReady, collectionPublishedReady } =
        data?.onboardingListData || {};

      const readyToGoLive = [
        profileReady,
        paymentMethodReady,
        collectionPublishedReady,
      ].every((completed) => !!completed);

      if (!readyToGoLive) {
        toast.error(
          'Creator cannot be set live, check onboarding flow steps on home page',
        );
        return;
      }
    }

    const payload: AdminInfluencerUpdateInput = {
      id: p.influencerId,
      ...updatedFields,
    };

    if (payload?.mode && payload.mode === enums.Influencer.Mode.Live.value) {
      track({
        type: 'Managed Creator Launched',
        payload: {
          'Collection Type': data?.creator?.collectionType || '',
          'Product Type': data?.creator?.productType || '',
          'Number of workouts published':
            data?.creator?.publishedWorkoutsCount || 0,
          'Number of collections published':
            data?.creator?.publisheCollectionsCount || 0,
          uid: p.influencerId,
        },
      });
    }

    setSubmitingUpdate(true);
    adminUpdateInfluencerMutation({
      variables: {
        data: payload,
      },
    })
      .then(() => {
        toast.success('Creator updated successfully');
        refetch();
      })
      .catch((error: any) => {
        toast.error(error.message);
      })
      .finally(() => {
        setSubmitingUpdate(false);
      });
  });

  const onResetStripe = () => {
    setSubmitingReset(true);
    adminResetStripeDataMutation({
      variables: {
        influencerId: p.influencerId,
      },
    })
      .then(() => {
        toast.success('Stripe resetted successfully');
        refetch();
      })
      .catch(() => {
        toast.error('Something went wrong with stripe reset');
      })
      .finally(() => {
        setSubmitingReset(false);
      });
  };

  const dispatch: AdministrationDispatcher = (type, ...args) => {
    const action = { type, payload: args[0] } as AdministrationAction;
    switch (action.type) {
      case 'resetStripe':
        onResetStripe();
        return null;
      case 'updateInfluencer':
        onUpdate();
        return null;
      case 'setTags': {
        clearErrors(['tags']);
        setValue('tags', action.payload);
        return null;
      }
      case 'setCustomizations':
        setValue('customizations', action.payload);
        return null;
    }
  };

  return {
    type: 'fetched',
    value: {
      dispatch,
      register,
      watch,
      control,
      stripeUserId: data?.creator?.stripeUserId || null,
      submittingReset,
      submittingUpdate,
      errors: formState.errors,
    },
  };
};
